import React, { useState, useEffect, useRef } from 'react';
import {
  Upload, FileSpreadsheet, Sparkles, ChevronRight, Search,
  AlertTriangle, CheckCircle2, X, Loader2, Users, UserMinus, UserPlus, Plus,
  LayoutGrid, List as ListIcon, Film, Layers, Scissors,
} from 'lucide-react';
import WorkCard from '@/components/WorkCard';
import CBAToolsModal from '@/components/CBAToolsModal';
import useRealtime from '@/hooks/useRealtime';
import AddProductionAssetModal from '@/components/AddProductionAssetModal';
// Founder ask (May 14, 2026): the CBA chip in this Submission Pipeline IS the
// only place CBA allotment should live (no top-row redundancy). When a PM
// clicks "CBA" here, we surface the full per-shot Allotment Console
// (auto-scan NAS + per-shot Layout/Animation assignment + bulk-assign).
import WorkforceBadge from '@/components/WorkforceBadge';
import AdminAllotmentPage from '@/pages/AdminAllotmentPage';
import LightingBriefModal from '@/components/LightingBriefModal';

// 13 production departments (Storyboard merged into Animatics per studio convention).
// `match` lists the underlying task_type keys that feed this tab — the screenplay
// ingester still creates separate `storyboard` + `animatics` tasks under the hood,
// but the UI now displays them as one merged "ANIMATICS" bucket.
const DEPT_META = [
  { key: 'script',      label: 'SCRIPT',      color: '#94a3b8', match: ['script'] },
  { key: 'animatics',   label: 'ANIMATICS',   color: '#a855f7', match: ['storyboard', 'animatics'] },
  // PIPELINE REFORM R4 — CBA is its own first-class tab between Animatics and Concept Art.
  { key: 'cba',         label: 'CBA',         color: '#8b5cf6', match: ['cba'] },
  { key: 'concept_art', label: 'CONCEPT ART', color: '#d946ef', match: ['concept_art'] },
  // PIPELINE REFORM R3 — Modeling + Texturing merged into ASSET BUILD
  { key: 'asset_build', label: 'ASSET BUILD', color: '#06b6d4', match: ['asset_build', 'modeling', 'texturing'] },
  { key: 'rigging',     label: 'RIGGING',     color: '#f59e0b', match: ['rigging'] },
  { key: 'layout',      label: 'LAYOUT',      color: '#0ea5e9', match: ['layout'] },
  { key: 'animation',   label: 'ANIMATION',   color: '#3b82f6', match: ['animation'] },
  { key: 'lighting',    label: 'LIGHTING',    color: '#8b5cf6', match: ['lighting'] },
  { key: 'fx',          label: 'FX',          color: '#f97316', match: ['fx'] },
  { key: 'comp',        label: 'COMP',        color: '#ec4899', match: ['comp'] },
  { key: 'sound',       label: 'SOUND',       color: '#22c55e', match: ['sound'] },
  { key: 'render',      label: 'RENDER',      color: '#facc15', match: ['render'] },
];

// Departments where ONE artist typically does the whole stage end-to-end —
// surfaces a "Assign all to one artist" shortcut on the tab header.
const SOLO_DEPTS = new Set(['animatics', 'sound', 'script', 'concept_art', 'cba']);

export const SubmissionPipelinePanel = ({ api, projectId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDept, setActiveDept] = useState('all');
  const [search, setSearch] = useState('');
  const [showAssign, setShowAssign] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [ingestResult, setIngestResult] = useState(null);
  const [ingestError, setIngestError] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState('');
  // Founder ask (May 13, 2026 production): the "Unblock Stuck Downstream"
  // healer used to live inside per-stage empty-state cards which made it
  // hard to find. It now lives as a global pill at the top of the
  // pipeline panel so it's always visible to PMs/Admins.
  const [unblockBusy, setUnblockBusy] = useState(false);
  const [unblockMsg, setUnblockMsg] = useState('');
  const [unblockErr, setUnblockErr] = useState('');
  // Second iteration (May 13, 2026 pm): if the healer scans 0 approved
  // upstreams, the user needs a way to see WHY each task is stuck — and a
  // last-resort "Force Unblock" path when concept-art was approved
  // outside the submission pipeline. We render a small diagnostic panel.
  const [stuckReport, setStuckReport] = useState(null);
  const [reportBusy, setReportBusy] = useState(false);
  const [forceBusy, setForceBusy] = useState(false);
  // PIPELINE REFORM: default to card view (rich, with raw materials) for ALL stages.
  // Power users can toggle to dense table view via the toggle.
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table'
  const [cbaTask, setCbaTask] = useState(null);     // open CBA editor for this task
  // Founder ask May 16, 2026: same workload-card we have on CBA needs
  // to live on the ASSET BUILD tab so PMs see who's responsible for
  // which assets. Also a quick "+ Add Asset" entry for production-only
  // props the client never approved.
  const [assetWorkload, setAssetWorkload] = useState(null);
  const [showAddAsset, setShowAddAsset] = useState(false);
  // Founder spec Feb 2026: when PM clicks "Brief" on a Lighting card the
  // LightingBriefModal opens with the shot id (task.parent_id).
  const [briefTask, setBriefTask] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const r = await api.get(`/projects/${projectId}/submission-pipeline`);
      setData(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Reset selection when project / dept changes
  useEffect(() => { setSelectedIds(new Set()); }, [projectId, activeDept]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  // Load per-artist asset-build workload whenever the ASSET BUILD tab
  // is active (or the project changes while it's already active).
  // Founder ask May 16, 2026 — same pattern as the CBA workload card.
  useEffect(() => {
    if (!projectId || activeDept !== 'asset_build') { setAssetWorkload(null); return; }
    let cancelled = false;
    api.get(`/animator/allotment/admin/asset-workload-summary?project_id=${projectId}&task_type=asset_build`)
      .then(r => { if (!cancelled) setAssetWorkload(r.data); })
      .catch(() => { if (!cancelled) setAssetWorkload(null); });
    return () => { cancelled = true; };
  }, [api, projectId, activeDept, data]);

  const handleUnblockStuckDownstream = async () => {
    if (!projectId) return;
    setUnblockBusy(true); setUnblockErr(''); setUnblockMsg('');
    try {
      const r = await api.post(`/admin/pipeline/unblock-stuck-downstream/${projectId}`);
      const stages = r.data.healed_by_upstream_stage || {};
      const total = Object.values(stages).reduce((s, n) => s + n, 0);
      const detail = Object.entries(stages)
        .filter(([, v]) => v > 0)
        .map(([k, v]) => `${k}: ${v}`)
        .join(' · ');
      const viaSub = r.data.healed_via_submission || 0;
      const viaTask = r.data.healed_via_task || 0;
      const viaBreakdown = viaSub || viaTask
        ? ` · via submissions: ${viaSub}, via completed-tasks: ${viaTask}` : '';
      if (total === 0) {
        // Auto-load the diagnostic so the user knows WHY 0 healed.
        setUnblockMsg(`✓ Scan complete — auto-unblock found no matching upstream signal. Loading diagnostic report so you can see which assets need Force Unblock…`);
        await loadStuckReport();
      } else {
        setUnblockMsg(`✓ Unblocked ${total} stuck downstream task${total === 1 ? '' : 's'}${detail ? ` · ${detail}` : ''}${viaBreakdown}. Refreshing pipeline…`);
        await load();
        setStuckReport(null);
      }
      window.setTimeout(() => setUnblockMsg(''), 12000);
    } catch (e) {
      setUnblockErr(e.response?.data?.detail || 'Unblock failed');
      window.setTimeout(() => setUnblockErr(''), 8000);
    } finally {
      setUnblockBusy(false);
    }
  };

  const loadStuckReport = async () => {
    if (!projectId) return;
    setReportBusy(true);
    try {
      const r = await api.get(`/admin/pipeline/stuck-downstream-report/${projectId}`);
      setStuckReport(r.data);
    } catch (e) {
      setUnblockErr(e.response?.data?.detail || 'Diagnostic failed');
    } finally {
      setReportBusy(false);
    }
  };

  const forceUnblockAll = async () => {
    if (!projectId) return;
    if (!stuckReport) return;
    const total = stuckReport.total_blocked;
    if (!window.confirm(
      `Force-unblock ALL ${total} blocked tasks in this project?\n\n` +
      `This bypasses the upstream-evidence check. Use this only when you are ` +
      `certain the upstream work is done outside the system (e.g. concept art ` +
      `approved via MDL only).\n\nThis action is audit-logged with your user id.`
    )) return;
    setForceBusy(true); setUnblockErr(''); setUnblockMsg('');
    try {
      const r = await api.post(`/admin/pipeline/force-unblock-stuck/${projectId}`, { confirm: 'yes' });
      setUnblockMsg(`🔓 Force-unblocked ${r.data.force_unblocked} tasks. Refreshing pipeline…`);
      setStuckReport(null);
      await load();
      window.setTimeout(() => setUnblockMsg(''), 12000);
    } catch (e) {
      setUnblockErr(e.response?.data?.detail || 'Force-unblock failed');
    } finally {
      setForceBusy(false);
    }
  };
  // PIPELINE REFORM R8 — Live push: when ANY task is auto-promoted,
  // assigned, or a submission is decided in THIS project, refetch the
  // pipeline so the PM sees the change instantly.
  useRealtime(React.useCallback((evt) => {
    if (!evt || !evt.type) return;
    // Only react if the event belongs to this project (or has no project_id —
    // in which case we err on the side of refreshing).
    if (evt.project_id && evt.project_id !== projectId) return;
    if (['task_auto_promoted', 'task_assigned',
         'submission_approved', 'submission_rejected', 'submission_revise'
        ].includes(evt.type)) {
      load();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]));

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setIngestError(''); setIngestResult(null); setUploading(true);
    const fd = new FormData();
    fd.append('file', f);
    try {
      const r = await api.post(`/projects/${projectId}/screenplay/ingest`, fd);
      setIngestResult(r.data);
      await load();
    } catch (err) {
      setIngestError(err.response?.data?.detail || err.message || 'Ingest failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const assign = async (taskId, userId) => {
    try {
      await api.post(`/projects/${projectId}/submission-pipeline/assign`, {
        task_id: taskId, assign_to: userId,
      });
      setShowAssign(null);
      await load();
    } catch (e) { alert(e.response?.data?.detail || 'Assign failed'); }
  };

  const bulkAssign = async (taskIds, userId) => {
    setBulkBusy(true); setBulkMsg('');
    try {
      const r = await api.post(`/projects/${projectId}/submission-pipeline/bulk-assign`, {
        task_ids: taskIds, assign_to: userId,
      });
      setBulkMsg(`Assigned ${r.data.assigned} task${r.data.assigned === 1 ? '' : 's'}`);
      setSelectedIds(new Set());
      setShowAssign(null);
      await load();
      setTimeout(() => setBulkMsg(''), 3500);
    } catch (e) { alert(e.response?.data?.detail || 'Bulk assign failed'); }
    finally { setBulkBusy(false); }
  };

  const bulkUnassign = async (taskIds) => {
    if (!taskIds.length) return;
    if (!window.confirm(`Unassign ${taskIds.length} task${taskIds.length === 1 ? '' : 's'}? They'll go back to the unassigned pool.`)) return;
    setBulkBusy(true); setBulkMsg('');
    try {
      const r = await api.post(`/projects/${projectId}/submission-pipeline/unassign`, { task_ids: taskIds });
      setBulkMsg(`Unassigned ${r.data.unassigned} task${r.data.unassigned === 1 ? '' : 's'}`);
      setSelectedIds(new Set());
      await load();
      setTimeout(() => setBulkMsg(''), 3500);
    } catch (e) { alert(e.response?.data?.detail || 'Unassign failed'); }
    finally { setBulkBusy(false); }
  };

  const dedupeAnimatics = async () => {
    if (!window.confirm('Collapse duplicate Storyboard rows into Animatics? This is a one-shot cleanup — safe to run.')) return;
    setBulkBusy(true); setBulkMsg('');
    try {
      const r = await api.post(`/projects/${projectId}/pipeline/dedupe-animatics`);
      setBulkMsg(`Cleaned up: ${r.data.removed} removed, ${r.data.flipped} converted to animatics`);
      await load();
      setTimeout(() => setBulkMsg(''), 4500);
    } catch (e) { alert(e.response?.data?.detail || 'Dedupe failed'); }
    finally { setBulkBusy(false); }
  };

  const collapseSequenceTasks = async () => {
    if (!window.confirm(
      'PIPELINE REFORM: collapse legacy per-shot Animatics/Sound rows into ONE sequence-mode task per stage?\n\n' +
      '• Preserves the strongest assignee (so e.g. Rahul stays as the Animator)\n' +
      '• Aggregates all per-shot media into the survivor\'s audit trail\n' +
      '• Re-wires Layout/Animation to gate on the project animatic (state machine)\n\n' +
      'Idempotent — safe to re-run.'
    )) return;
    setBulkBusy(true); setBulkMsg('');
    try {
      const r = await api.post(`/projects/${projectId}/pipeline/collapse-sequence-tasks`);
      const j = r.data;
      setBulkMsg(`✓ Collapsed ${j.total_collapsed} legacy rows · kept ${j.total_kept_assignees} assignees · preserved ${j.total_media_preserved} media items · stamped ${j.work_unit_type_stamped} task work_unit_type`);
      await load();
      setTimeout(() => setBulkMsg(''), 8000);
    } catch (e) { alert(e.response?.data?.detail || 'Collapse failed'); }
    finally { setBulkBusy(false); }
  };

  const mergeModelingTexturing = async () => {
    if (!window.confirm(
      'PIPELINE REFORM R3: merge legacy Modeling + Texturing tasks into a single ASSET BUILD task per asset?\n\n' +
      '• Preserves the strongest assignee\n' +
      '• Stitches all submitted media into the survivor\'s audit trail\n' +
      '• Rewires Rigging.depends_on to the new asset_build task\n\n' +
      'Idempotent — safe to re-run.'
    )) return;
    setBulkBusy(true); setBulkMsg('');
    try {
      const r = await api.post(`/projects/${projectId}/pipeline/merge-modeling-texturing`);
      const j = r.data;
      setBulkMsg(`✓ Merged ${j.asset_build_tasks_after} asset_build tasks · deleted ${j.legacy_tasks_deleted} legacy rows · preserved ${j.media_history_entries_preserved} media items`);
      await load();
      setTimeout(() => setBulkMsg(''), 8000);
    } catch (e) { alert(e.response?.data?.detail || 'Merge failed'); }
    finally { setBulkBusy(false); }
  };

  if (loading) return <div className="text-zinc-500 text-xs py-8 text-center" data-testid="sp-loading">Loading submission pipeline...</div>;
  if (!data) return null;

  const rawTotals = data.totals || {};
  const tasksByRawDept = data.tasks_by_department || {};

  // Build merged dept buckets (Storyboard + Animatics combined). Each task gets a
  // synthetic `_dept` = the merged bucket key so the table can highlight & filter.
  const mergedTasks = (() => {
    const out = {};
    DEPT_META.forEach(d => {
      out[d.key] = (d.match || [d.key]).flatMap(k =>
        (tasksByRawDept[k] || []).map(t => ({ ...t, _dept: d.key, _rawDept: k }))
      );
    });
    return out;
  })();

  const mergedTotals = (() => {
    const out = {};
    DEPT_META.forEach(d => { out[d.key] = (d.match || [d.key]).reduce((s, k) => s + (rawTotals[k] || 0), 0); });
    return out;
  })();

  // Count only "ready" tasks per tab — BLOCKED tasks are placeholders
  // gated on upstream approval; they shouldn't inflate the tab badge and
  // mislead PMs into thinking work is available. Ready = anything not blocked.
  const mergedReadyTotals = (() => {
    const out = {};
    DEPT_META.forEach(d => {
      out[d.key] = (d.match || [d.key]).reduce((s, k) => {
        const arr = tasksByRawDept[k] || [];
        return s + arr.filter(t => (t.status || '').toLowerCase() !== 'blocked').length;
      }, 0);
    });
    return out;
  })();

  const allTasks = Object.values(mergedTasks).flat();
  const visible = activeDept === 'all' ? allTasks : (mergedTasks[activeDept] || []);
  const filtered = visible.filter(t =>
    !search || (t.title || '').toLowerCase().includes(search.toLowerCase()) ||
    (t.parent_code || '').toLowerCase().includes(search.toLowerCase())
  );
  const filteredCapped = filtered.slice(0, 500);

  const totalAll = Object.values(mergedTotals).reduce((a, b) => a + b, 0);
  const internal = data.workforce?.internal || [];
  const freelancers = data.workforce?.freelancers || [];
  const vendors = data.workforce?.vendors || [];

  // Selection helpers
  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allVisibleSelected = filteredCapped.length > 0 && filteredCapped.every(t => selectedIds.has(t.id));
  const toggleAllVisible = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) filteredCapped.forEach(t => next.delete(t.id));
      else filteredCapped.forEach(t => next.add(t.id));
      return next;
    });
  };
  const selectedTasks = allTasks.filter(t => selectedIds.has(t.id));
  const selectedAssigned = selectedTasks.filter(t => t.assigned_to);
  const selectedUnassigned = selectedTasks.filter(t => !t.assigned_to);

  const activeDeptMeta = DEPT_META.find(d => d.key === activeDept);
  const isSoloDept = activeDept !== 'all' && SOLO_DEPTS.has(activeDept);
  const tabUnassignedCount = activeDept === 'all'
    ? allTasks.filter(t => !t.assigned_to).length
    : (mergedTasks[activeDept] || []).filter(t => !t.assigned_to).length;

  return (
    <div className="space-y-4" data-testid="submission-pipeline-panel">
      {/* Hero / Upload */}
      <div className="bg-gradient-to-r from-violet-900/30 via-fuchsia-900/20 to-rose-900/20 border border-violet-500/30 rounded-sm p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-fuchsia-300" />
              <h3 className="text-sm font-heading font-semibold text-white">Submission Pipeline · Autonomous Ingest</h3>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
              Upload a client screenplay or breakdown spreadsheet (.xlsx). The engine auto-creates
              placeholder Characters, Backgrounds, and Shots in the Master Data Library and seeds
              tasks across all <span className="text-fuchsia-300 font-medium">14 production departments</span> —
              ready for one-click allotment to your studio, freelancers, or vendor partners.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <input ref={fileRef} type="file" accept=".xlsx" onChange={handleUpload} className="hidden" data-testid="sp-file-input" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="sp-upload-btn"
              className="px-4 py-2 bg-gradient-to-r from-fuchsia-500 to-rose-500 hover:from-fuchsia-400 hover:to-rose-400 text-white rounded-sm text-xs font-medium flex items-center gap-2 disabled:opacity-50 shadow-lg shadow-fuchsia-500/20 transition-all"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploading ? 'Ingesting…' : 'Upload Screenplay'}
            </button>
            <span className="text-[9px] text-zinc-500">.xlsx · TIMECODE · LOCATION · CHARACTER · DIALOGUE</span>
          </div>
        </div>

        {ingestResult && (
          <div className="mt-3 p-2.5 bg-emerald-500/10 border border-emerald-500/30 rounded-sm flex items-start gap-2" data-testid="sp-ingest-success">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5" />
            <div className="flex-1 text-[10px] text-zinc-300">
              <span className="text-emerald-400 font-medium">Ingested {ingestResult.filename}</span>
              {' · '}{ingestResult.counts.characters} characters · {ingestResult.counts.backgrounds} backgrounds ·
              {' '}{ingestResult.counts.shots} shots · {ingestResult.counts.tasks} tasks created
              {ingestResult.counts.skipped > 0 && <span className="text-zinc-500"> · {ingestResult.counts.skipped} duplicates skipped</span>}
            </div>
            <button onClick={() => setIngestResult(null)}><X className="w-3 h-3 text-zinc-500 hover:text-white" /></button>
          </div>
        )}
        {ingestError && (
          <div className="mt-3 p-2.5 bg-rose-500/10 border border-rose-500/30 rounded-sm flex items-start gap-2" data-testid="sp-ingest-error">
            <AlertTriangle className="w-3.5 h-3.5 text-rose-400 mt-0.5" />
            <p className="flex-1 text-[10px] text-rose-300">{ingestError}</p>
            <button onClick={() => setIngestError('')}><X className="w-3 h-3 text-zinc-500 hover:text-white" /></button>
          </div>
        )}
      </div>

      {/* GLOBAL HEALER — "Unblock Stuck Downstream"
          Founder spec (May 13, 2026 production): the per-stage empty-state
          button was hard to find. This top-level pill is always visible
          for PMs/Admins on the pipeline page. */}
      <div className="flex items-center gap-3 bg-gradient-to-r from-amber-500/8 to-transparent border border-amber-500/30 rounded-md px-3 py-2 flex-wrap" data-testid="sp-global-unblock-banner">
        <span className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">🔓 Pipeline Heal</span>
        <span className="text-[10px] text-zinc-400 flex-1 min-w-[200px]">
          Downstream task stuck "Waiting on upstream" even though the upstream stage is already approved? One-click fix.
        </span>
        <button
          onClick={handleUnblockStuckDownstream}
          disabled={unblockBusy || !projectId}
          data-testid="sp-global-unblock-btn"
          className="h-8 px-3 bg-amber-500 hover:bg-amber-400 text-zinc-950 text-[11px] font-bold rounded flex items-center gap-1.5 disabled:opacity-50"
        >
          {unblockBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <span aria-hidden>🔓</span>}
          {unblockBusy ? 'Scanning…' : 'Unblock Stuck Downstream'}
        </button>
        <button
          onClick={loadStuckReport}
          disabled={reportBusy || !projectId}
          data-testid="sp-show-diagnostic-btn"
          className="h-8 px-2 text-[10px] bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded flex items-center gap-1 disabled:opacity-50"
          title="Preview what's stuck and why before running the heal"
        >
          {reportBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <span aria-hidden>🔍</span>}
          View Stuck Tasks
        </button>
      </div>
      {unblockMsg && (
        <div className="bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 rounded-md px-3 py-2 text-[11px]" data-testid="sp-global-unblock-msg">
          {unblockMsg}
        </div>
      )}
      {unblockErr && (
        <div className="bg-red-500/15 border border-red-500/40 text-red-200 rounded-md px-3 py-2 text-[11px]" data-testid="sp-global-unblock-err">
          {unblockErr}
        </div>
      )}
      {stuckReport && (
        <div className="bg-zinc-900 border border-zinc-700 rounded-md overflow-hidden" data-testid="sp-stuck-report">
          <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-2 bg-amber-500/8">
            <div className="text-[11px] text-zinc-300">
              <span className="font-bold uppercase tracking-widest text-amber-300">Diagnostic · </span>
              <span className="font-mono">{stuckReport.total_blocked}</span> blocked tasks ·
              <span className="text-emerald-400 ml-1">{stuckReport.healable_by_auto_unblock} healable</span> ·
              <span className="text-rose-400 ml-1">{stuckReport.needs_force_unblock} need Force</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={loadStuckReport}
                disabled={reportBusy}
                data-testid="sp-stuck-report-refresh"
                className="h-7 px-2 text-[10px] bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded flex items-center gap-1 disabled:opacity-50"
              >
                {reportBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <span>↻</span>}
                Refresh
              </button>
              {stuckReport.needs_force_unblock > 0 && (
                <button
                  onClick={forceUnblockAll}
                  disabled={forceBusy}
                  data-testid="sp-force-unblock-btn"
                  className="h-7 px-2 text-[10px] bg-rose-500/25 hover:bg-rose-500/40 text-rose-200 border border-rose-500/40 rounded flex items-center gap-1 disabled:opacity-50"
                  title="Force-unblock all blocked tasks regardless of upstream evidence — audit-logged"
                >
                  {forceBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <span>⚠️</span>}
                  Force Unblock All
                </button>
              )}
              <button
                onClick={() => setStuckReport(null)}
                data-testid="sp-stuck-report-close"
                className="h-7 px-2 text-[10px] bg-zinc-800 hover:bg-zinc-700 text-zinc-400 rounded"
              >
                Close
              </button>
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-[10px]">
              <thead className="bg-zinc-950/60 sticky top-0">
                <tr className="text-zinc-500 uppercase tracking-wider">
                  <th className="px-3 py-1.5 text-left">Asset</th>
                  <th className="px-3 py-1.5 text-left">Stage</th>
                  <th className="px-3 py-1.5 text-left">Upstream Evidence</th>
                  <th className="px-3 py-1.5 text-left">Action Needed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/40">
                {stuckReport.blocked_tasks.slice(0, 50).map(t => (
                  <tr key={t.task_id} className="hover:bg-zinc-800/30" data-testid={`sp-stuck-row-${t.task_id}`}>
                    <td className="px-3 py-1.5 text-zinc-200 truncate max-w-[200px]" title={t.parent_name || t.parent_code}>
                      {t.parent_name || t.parent_code || t.parent_id?.slice(0, 8) || '—'}
                    </td>
                    <td className="px-3 py-1.5 text-zinc-400 font-mono">{t.task_type}</td>
                    <td className="px-3 py-1.5">
                      {t.upstream_evidence.length === 0 ? (
                        <span className="text-rose-300">No approved upstream found</span>
                      ) : (
                        <span className="text-emerald-300">
                          {t.upstream_evidence.map(e => `${e.kind}:${e.stage}`).join(', ')}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      {t.healable_by_auto_unblock ? (
                        <span className="text-emerald-400">Auto-Unblock</span>
                      ) : (
                        <span className="text-rose-300">Force Unblock</span>
                      )}
                    </td>
                  </tr>
                ))}
                {stuckReport.blocked_tasks.length > 50 && (
                  <tr>
                    <td colSpan={4} className="px-3 py-1.5 text-center text-zinc-500 italic text-[9px]">
                      Showing first 50 of {stuckReport.blocked_tasks.length}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Workforce strip */}
      <div className="flex items-center gap-4 text-[10px] bg-zinc-900/50 border border-zinc-800 rounded-sm px-3 py-2" data-testid="sp-workforce-strip">
        <Users className="w-3.5 h-3.5 text-zinc-500" />
        <span className="text-zinc-500">Available workforce:</span>
        <span className="text-blue-400">{internal.length} Internal</span>
        <span className="text-amber-400">{freelancers.length} Freelancers</span>
        <span className="text-violet-400">{vendors.length} Vendors</span>
        <span className="ml-auto text-zinc-500">Total tasks: <span className="text-white font-mono">{totalAll}</span></span>
      </div>

      {/* Legacy storyboard cleanup nudge — surfaces only when both task_types exist. */}
      {(rawTotals.storyboard || 0) > 0 && (rawTotals.animatics || 0) > 0 && (
        <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-sm px-3 py-2" data-testid="sp-dedupe-banner">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
          <p className="text-[11px] text-amber-100 flex-1">
            <span className="font-semibold">Duplicate rows detected:</span> {rawTotals.storyboard} legacy <code className="text-amber-300">Storyboard</code> tasks
            sit alongside {rawTotals.animatics} <code className="text-amber-300">Animatics</code> tasks for the same shots.
            Click to merge them into a single ANIMATICS row per shot.
          </p>
          <button onClick={dedupeAnimatics} disabled={bulkBusy} data-testid="sp-dedupe-btn"
            className="px-3 py-1 text-[10px] font-medium rounded bg-amber-500 hover:bg-amber-400 text-zinc-950 disabled:opacity-50">
            {bulkBusy ? 'Working…' : 'Merge duplicates'}
          </button>
        </div>
      )}

      {/* PIPELINE REFORM R3 banner — surfaces when legacy modeling/texturing
          tasks exist. One-click merges them into asset_build. */}
      {((rawTotals.modeling || 0) > 0 || (rawTotals.texturing || 0) > 0) && (
        <div className="flex items-start gap-2 bg-cyan-500/10 border border-cyan-500/40 rounded-sm px-3 py-2.5" data-testid="sp-merge-banner">
          <Layers className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-[11px] text-cyan-100 font-semibold mb-0.5">Pipeline reform R3 — legacy Modeling + Texturing detected</p>
            <p className="text-[10px] text-zinc-400">
              Studio convention: Modeling + Texturing are done together as a single "Asset Build" deliverable.
              <span className="mx-1 px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 font-mono text-[9px]">
                MODELING: {rawTotals.modeling || 0}
              </span>
              <span className="mx-1 px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 font-mono text-[9px]">
                TEXTURING: {rawTotals.texturing || 0}
              </span>
              . Click to merge into single ASSET BUILD tasks (preserves assignees + media).
            </p>
          </div>
          <button onClick={mergeModelingTexturing} disabled={bulkBusy} data-testid="sp-merge-btn"
            className="px-3 py-1.5 text-[10px] font-medium rounded bg-gradient-to-r from-cyan-500 to-fuchsia-500 hover:from-cyan-400 hover:to-fuchsia-400 text-white disabled:opacity-50 flex items-center gap-1.5 shadow-lg shadow-cyan-500/20 flex-shrink-0">
            {bulkBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Layers className="w-3 h-3" />}
            {bulkBusy ? 'Merging…' : 'Merge to Asset Build'}
          </button>
        </div>
      )}

      {/* PIPELINE REFORM banner — surfaces when sequence-mode dept (animatics, sound, etc.)
          has >1 task per project (legacy multiplied-per-shot symptom). One-click migration. */}
      {(() => {
        const seqBloat = ['animatics', 'sound', 'storyboard', 'music'].filter(s => (rawTotals[s] || 0) > 1);
        if (!seqBloat.length) return null;
        return (
          <div className="flex items-start gap-2 bg-violet-500/10 border border-violet-500/40 rounded-sm px-3 py-2.5" data-testid="sp-collapse-banner">
            <Layers className="w-4 h-4 text-violet-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-[11px] text-violet-100 font-semibold mb-0.5">Pipeline reform — legacy data detected</p>
              <p className="text-[10px] text-zinc-400">
                Stages that should be 1 deliverable per episode are multiplied per shot:{' '}
                {seqBloat.map(s => (
                  <span key={s} className="mx-1 px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 font-mono text-[9px]">
                    {s.toUpperCase()}: {rawTotals[s]}
                  </span>
                ))}
                . Click to collapse into 1 sequence task each (preserves assignees + media).
              </p>
            </div>
            <button onClick={collapseSequenceTasks} disabled={bulkBusy} data-testid="sp-collapse-btn"
              className="px-3 py-1.5 text-[10px] font-medium rounded bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-400 hover:to-fuchsia-400 text-white disabled:opacity-50 flex items-center gap-1.5 shadow-lg shadow-violet-500/20 flex-shrink-0">
              {bulkBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Layers className="w-3 h-3" />}
              {bulkBusy ? 'Collapsing…' : 'Collapse to 1-per-episode'}
            </button>
          </div>
        );
      })()}

      {/* 13-department tabs (Storyboard + Animatics merged) */}
      <div className="flex flex-wrap gap-1 border-b border-zinc-800" data-testid="sp-dept-tabs">
        <button
          onClick={() => setActiveDept('all')}
          className={`px-3 py-2 text-[10px] font-medium border-b-2 -mb-px ${activeDept === 'all' ? 'border-white text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}
          data-testid="sp-tab-all"
        >
          ALL <span className="ml-1 text-zinc-600">{totalAll}</span>
        </button>
        {DEPT_META.map(d => {
          const nTotal = mergedTotals[d.key] || 0;
          const nReady = mergedReadyTotals[d.key] || 0;
          const nBlocked = nTotal - nReady;
          const isActive = activeDept === d.key;
          // Badge rules:
          //  • nReady > 0        → show ready count (normal emphasis)
          //  • nTotal > 0, all blocked → show small muted "blocked" pill
          //  • no tasks          → no badge
          const tooltip = nTotal === 0 ? '' :
            (nBlocked > 0
              ? `${nReady} ready · ${nBlocked} blocked waiting on upstream approval`
              : `${nReady} ready to assign`);
          return (
            <button
              key={d.key}
              onClick={() => setActiveDept(d.key)}
              className={`px-3 py-2 text-[10px] font-medium border-b-2 -mb-px whitespace-nowrap ${isActive ? 'text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
              style={{ borderBottomColor: isActive ? d.color : 'transparent' }}
              data-testid={`sp-tab-${d.key}`}
              title={tooltip}
            >
              {d.label}
              {nReady > 0 && (
                <span className="ml-1 px-1 py-0.5 rounded bg-zinc-800 text-[9px] font-mono"
                      style={{ color: d.color }}
                      data-testid={`sp-tab-${d.key}-ready-count`}>{nReady}</span>
              )}
              {nReady === 0 && nTotal > 0 && (
                <span className="ml-1 px-1 py-0.5 rounded bg-zinc-900 border border-zinc-800 text-[9px] font-mono text-zinc-600"
                      data-testid={`sp-tab-${d.key}-blocked-count`}
                      title={`${nTotal} blocked — waiting on upstream approval`}>
                  {nTotal}
                  <span className="ml-0.5 opacity-60">·blocked</span>
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search + per-tab quick action (solo-dept "Assign all to one artist") */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative max-w-xs flex-1 min-w-[16rem]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search task or shot code..."
            className="w-full h-8 pl-9 pr-3 bg-zinc-900 border border-zinc-800 rounded-sm text-xs text-white focus:outline-none"
            data-testid="sp-search" />
        </div>
        {/* Founder ask May 16, 2026: Add Asset button on the ASSET BUILD
            tab — for production-only props the client never approved. */}
        {activeDept === 'asset_build' && (
          <button
            onClick={() => setShowAddAsset(true)}
            data-testid="sp-add-asset-btn"
            className="px-3 py-1.5 bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-500/40 text-cyan-200 rounded-sm text-[10px] font-medium flex items-center gap-1.5"
            title="Add a production-only asset (skips client approval)"
          >
            <Plus className="w-3 h-3" />
            Add Asset
          </button>
        )}
        {isSoloDept && tabUnassignedCount > 0 && (
          <button
            onClick={() => setShowAssign({
              _bulk: true,
              _scope: 'tab',
              _ids: (mergedTasks[activeDept] || []).filter(t => !t.assigned_to).map(t => t.id),
              _dept: activeDept,
              title: `All ${activeDeptMeta?.label} tasks (${tabUnassignedCount})`,
            })}
            data-testid={`sp-assign-tab-${activeDept}`}
            className="px-3 py-1.5 bg-fuchsia-500/15 hover:bg-fuchsia-500/25 border border-fuchsia-500/40 text-fuchsia-200 rounded-sm text-[10px] font-medium flex items-center gap-1.5">
            <UserPlus className="w-3 h-3" />
            Assign all {tabUnassignedCount} {activeDeptMeta?.label} to one artist
          </button>
        )}
        {bulkMsg && (
          <span className="text-[10px] text-emerald-300 ml-auto" data-testid="sp-bulk-msg">{bulkMsg}</span>
        )}
        {/* PIPELINE REFORM: view toggle — cards (default, with raw materials) vs table (dense). */}
        {activeDept !== 'all' && (
          <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-800 rounded-sm p-0.5 ml-auto" data-testid="sp-view-toggle">
            <button onClick={() => setViewMode('cards')}
              data-testid="sp-view-cards"
              title="Show as work cards with raw-material inputs"
              className={`p-1 rounded ${viewMode !== 'table' ? 'bg-fuchsia-500/20 text-fuchsia-300' : 'text-zinc-500 hover:text-zinc-300'}`}>
              <LayoutGrid className="w-3 h-3" />
            </button>
            <button onClick={() => setViewMode('table')}
              data-testid="sp-view-table"
              title="Show as table (dense)"
              className={`p-1 rounded ${viewMode === 'table' ? 'bg-fuchsia-500/20 text-fuchsia-300' : 'text-zinc-500 hover:text-zinc-300'}`}>
              <ListIcon className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* Bulk-action toolbar — appears when rows are checked */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-2 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-sm px-3 py-2" data-testid="sp-bulk-toolbar">
          <span className="text-[11px] text-fuchsia-200 font-medium">
            {selectedIds.size} selected
            {selectedAssigned.length > 0 && <span className="text-zinc-400"> · {selectedAssigned.length} assigned</span>}
            {selectedUnassigned.length > 0 && <span className="text-zinc-400"> · {selectedUnassigned.length} unassigned</span>}
          </span>
          <div className="ml-auto flex items-center gap-1.5">
            {selectedUnassigned.length > 0 && (
              <button
                onClick={() => setShowAssign({
                  _bulk: true,
                  _scope: 'selection',
                  _ids: selectedUnassigned.map(t => t.id),
                  _dept: activeDept === 'all' ? 'mixed' : activeDept,
                  title: `${selectedUnassigned.length} selected unassigned task${selectedUnassigned.length === 1 ? '' : 's'}`,
                })}
                disabled={bulkBusy}
                data-testid="sp-bulk-assign-btn"
                className="px-2.5 py-1 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-200 rounded text-[10px] flex items-center gap-1 disabled:opacity-50">
                <UserPlus className="w-3 h-3" />
                Assign {selectedUnassigned.length} to one artist
              </button>
            )}
            {selectedAssigned.length > 0 && (
              <button
                onClick={() => bulkUnassign(selectedAssigned.map(t => t.id))}
                disabled={bulkBusy}
                data-testid="sp-bulk-unassign-btn"
                className="px-2.5 py-1 bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/40 text-rose-200 rounded text-[10px] flex items-center gap-1 disabled:opacity-50">
                <UserMinus className="w-3 h-3" />
                Unassign {selectedAssigned.length}
              </button>
            )}
            <button onClick={() => setSelectedIds(new Set())} data-testid="sp-clear-selection"
              className="px-2 py-1 text-[10px] text-zinc-400 hover:text-white">Clear</button>
          </div>
        </div>
      )}

      {/* CBA chip — Founder ask (May 14, 2026): instead of a list of plain
          CBA work-cards, render the full Allotment Console (per-shot grid,
          auto-scan NAS, bulk-assign Layout/Animation) right here. This is
          the single canonical place for CBA allotment now that the legacy
          top-row pipeline tabs are gone. */}
      {activeDept === 'cba' && (
        <AdminAllotmentPage embeddedProjectId={projectId} />
      )}

      {/* ASSET BUILD per-artist workload — founder ask May 16, 2026: same
          "Who has what" card we have on CBA, but for asset_build tasks. */}
      {activeDept === 'asset_build' && assetWorkload && assetWorkload.rows && assetWorkload.rows.length > 0 && (
        <div
          className="bg-zinc-900 border border-zinc-800 rounded-md"
          data-testid="sp-asset-workload"
        >
          <div className="px-3 py-2 border-b border-zinc-800">
            <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1.5">
              <Users className="w-3 h-3 text-cyan-400" />
              Asset Build Workload · {assetWorkload.total_artists} artists · {assetWorkload.total_tasks} tasks
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 p-2">
            {assetWorkload.rows.map(r => (
              <div
                key={r.user_id}
                className="bg-zinc-950/60 border border-zinc-800/60 rounded-sm px-2.5 py-2"
                data-testid={`sp-asset-workload-row-${r.user_id}`}
              >
                <div className="flex items-center justify-between gap-1.5">
                  <p className="text-[11px] text-zinc-200 truncate flex-1">
                    {r.full_name || '—'}
                  </p>
                  <WorkforceBadge user={r} />
                </div>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-base font-heading font-bold text-cyan-300"
                        data-testid={`sp-asset-workload-count-${r.user_id}`}>
                    {r.assigned_assets} asset{r.assigned_assets === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="text-[9px] text-zinc-500 mt-0.5">
                  {r.assigned_tasks} task{r.assigned_tasks === 1 ? '' : 's'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tasks — Pipeline-Reform: ALL stages now render as cards. Cards are
          collapsible — sequence/asset cards are rich (1-3 rows); shot cards
          will appear here once CBA generates them. Empty stages show a
          'will populate from CBA' hint. */}
      {activeDept !== 'cba' && (() => {
        const deptMode = data.work_unit_mode_by_department?.[activeDept] || 'shot';
        const useCards = activeDept !== 'all' && viewMode !== 'table';
        const isShotDeptEmpty = deptMode === 'shot' && (filteredCapped || []).length === 0;
        // PIPELINE REFORM R6 — Approval Engine empty-state. If the target dept
        // is asset-mode and has 0 tasks, show "X approved upstream waiting" +
        // "Promote N" button so the PM can backfill in one click.
        const isAssetDeptEmpty = deptMode === 'asset' && (filteredCapped || []).length === 0;
        if (useCards) {
          return (
            <div className="space-y-3" data-testid="sp-work-cards">
              {filtered.length === 0 ? (
                isAssetDeptEmpty ? (
                  <ApprovalEngineEmptyState api={api} projectId={projectId}
                    target={activeDept} onPromoted={load} />
                ) : (
                <div className="bg-zinc-900 border border-zinc-800 rounded-md text-center py-12">
                  {isShotDeptEmpty && totalAll > 0 ? (
                    <>
                      <Scissors className="w-10 h-10 text-violet-500/60 mx-auto mb-3" />
                      <p className="text-sm text-zinc-300 font-medium mb-1">Empty — by design</p>
                      <p className="text-[11px] text-zinc-500 max-w-md mx-auto leading-relaxed">
                        Shot-mode stages (<code>{activeDept}</code>) are populated by{' '}
                        <span className="text-violet-300 font-medium">CBA · Cut-By-Animatics</span>{' '}
                        AFTER the animatic is client-approved. Approve the animatic, then run CBA Tools to fan out shot cards here.
                      </p>
                    </>
                  ) : (
                    <>
                      <FileSpreadsheet className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                      <p className="text-xs text-zinc-500">
                        {totalAll === 0 ? 'No screenplay ingested yet. Upload an .xlsx to begin.' : 'No tasks match your filter.'}
                      </p>
                    </>
                  )}
                </div>
                )
              ) : (
                filteredCapped.map(t => (
                  <WorkCard
                    key={t.id}
                    task={t}
                    api={api}
                    projectId={projectId}
                    internal={internal}
                    freelancers={freelancers}
                    vendors={vendors}
                    onAssignClick={(task) => setShowAssign(task)}
                    onUnassignClick={(task) => bulkUnassign([task.id])}
                    onBriefClick={(t) => setBriefTask(t)}
                    /* PIPELINE REFORM R4 — CBA Tools button lives ONLY on the
                       cba card. Click resolves the linked animatic task and
                       passes IT to the modal (modal endpoints operate on the
                       animatic media). */
                    onCBAClick={(task) => {
                      const animaticId = task.animatic_task_id || task.depends_on;
                      if (!animaticId) {
                        alert('No linked animatic task — run the migration first to back-fill the CBA chain.');
                        return;
                      }
                      // Find the actual animatic task from the loaded data so the modal title shows it correctly.
                      const animaticTask = (data.tasks_by_department?.animatics || [])
                        .find(at => at.id === animaticId) || { id: animaticId, title: task.title.replace(' · CBA · Cut-By-Animatics', ' · Animatics') };
                      setCbaTask(animaticTask);
                    }}
                    onRefresh={load}
                    showCBATools={t.task_type === 'cba'}
                  />
                ))
              )}
              {filtered.length > 500 && (
                <p className="text-[9px] text-zinc-600 text-center py-2">
                  Showing first 500 of {filtered.length} tasks. Filter by department or search to narrow down.
                </p>
              )}
            </div>
          );
        }
        // Table mode (forced via toggle) — still available for power users
        return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center py-12">
            <FileSpreadsheet className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-xs text-zinc-500">
              {totalAll === 0 ? 'No screenplay ingested yet. Upload an .xlsx to begin.' : 'No tasks match your filter.'}
            </p>
          </div>
        ) : (
          <table className="w-full" data-testid="sp-tasks-table">
            <thead>
              <tr className="border-b border-zinc-800 text-[9px] text-zinc-500 uppercase">
                <th className="text-center px-2 py-2 font-medium w-8">
                  <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible}
                    data-testid="sp-select-all"
                    className="accent-fuchsia-500 cursor-pointer" />
                </th>
                <th className="text-left px-3 py-2 font-medium">Task</th>
                <th className="text-left px-2 py-2 font-medium w-24">Department</th>
                <th className="text-left px-2 py-2 font-medium w-24">Parent</th>
                <th className="text-left px-2 py-2 font-medium w-20">Deadline</th>
                <th className="text-left px-2 py-2 font-medium w-28">Status</th>
                <th className="text-right px-2 py-2 font-medium w-40">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/40">
              {filteredCapped.map(t => {
                const meta = DEPT_META.find(d => d.key === t._dept);
                const assignedName = t.assigned_to ? (
                  internal.find(u => u.id === t.assigned_to)?.full_name ||
                  freelancers.find(u => u.id === t.assigned_to)?.full_name ||
                  vendors.find(v => v.user_id === t.assigned_to)?.company_name ||
                  t.assigned_to.slice(0, 8)
                ) : null;
                const checked = selectedIds.has(t.id);
                return (
                  <tr key={t.id} className={`hover:bg-zinc-800/30 ${checked ? 'bg-fuchsia-500/5' : ''}`}>
                    <td className="px-2 py-2 text-center">
                      <input type="checkbox" checked={checked} onChange={() => toggleOne(t.id)}
                        data-testid={`sp-select-${t.id}`}
                        className="accent-fuchsia-500 cursor-pointer" />
                    </td>
                    <td className="px-3 py-2 text-[11px] text-zinc-200">{t.title}</td>
                    <td className="px-2 py-2">
                      <span className="text-[8px] px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: `${meta?.color}20`, color: meta?.color }}>
                        {meta?.label}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-[10px] font-mono text-zinc-400">{t.parent_code || '—'}</td>
                    <td className="px-2 py-2 text-[10px] text-zinc-400">{t.deadline ? t.deadline.slice(0, 10) : '—'}</td>
                    <td className="px-2 py-2">
                      <span className={`text-[8px] px-1.5 py-0.5 rounded ${
                        t.status === 'assigned' ? 'bg-blue-500/15 text-blue-400'
                        : t.status === 'completed' ? 'bg-emerald-500/15 text-emerald-400'
                        : 'bg-zinc-800 text-zinc-500'
                      }`}>{t.status?.toUpperCase()}</span>
                    </td>
                    <td className="px-2 py-2 text-right">
                      {t.assigned_to ? (
                        <div className="inline-flex items-center gap-2">
                          <span className="text-[10px] text-emerald-400 truncate max-w-[8rem]" title={assignedName}>→ {assignedName}</span>
                          <button
                            onClick={() => bulkUnassign([t.id])}
                            data-testid={`sp-unassign-btn-${t.id}`}
                            disabled={bulkBusy}
                            title="Unassign — pull this task back"
                            className="text-[9px] px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-300 hover:bg-rose-500/25 border border-rose-500/30 disabled:opacity-40 flex items-center gap-1">
                            <UserMinus className="w-2.5 h-2.5" />
                            Unassign
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowAssign(t)}
                          data-testid={`sp-assign-btn-${t.id}`}
                          className="text-[9px] px-2 py-1 rounded bg-fuchsia-500/15 text-fuchsia-300 hover:bg-fuchsia-500/30 transition-all"
                        >
                          + Assign <ChevronRight className="w-2.5 h-2.5 inline" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {filtered.length > 500 && (
          <p className="text-[9px] text-zinc-600 text-center py-2 border-t border-zinc-800">
            Showing first 500 of {filtered.length} tasks. Filter by department or search to narrow down.
          </p>
        )}
      </div>
        );
      })()}

      {/* CBA Tools modal — animatics-only, opened from a sequence WorkCard */}
      {cbaTask && (
        <CBAToolsModal
          open={!!cbaTask}
          sequenceTask={cbaTask}
          api={api}
          onClose={() => setCbaTask(null)}
          onLocked={() => { setCbaTask(null); load(); }}
        />
      )}

      {/* Founder spec Feb 2026: Lighting Brief modal — opens from the
          "Brief" pill on Lighting WorkCards. PM fills time_of_day +
          bg_type + free-text brief. Saved brief becomes a raw-material
          chip on the artist's MY TASKS card. */}
      {briefTask && (
        <LightingBriefModal
          shotId={briefTask.parent_id || briefTask.shot_id || briefTask.id}
          parentCode={briefTask.parent_code || briefTask.title}
          api={api}
          onClose={() => setBriefTask(null)}
          onSaved={() => { setBriefTask(null); load(); }}
        />
      )}

      {/* Assign Modal — single OR bulk */}
      {showAssign && (
        <AssignModal
          task={showAssign}
          internal={internal}
          freelancers={freelancers}
          vendors={vendors}
          deptRoleMap={data.department_role_map || {}}
          onClose={() => setShowAssign(null)}
          onAssign={(uid) => {
            if (showAssign._bulk) bulkAssign(showAssign._ids || [], uid);
            else assign(showAssign.id, uid);
          }}
        />
      )}
    </div>
  );
};

const AssignModal = ({ task, internal, freelancers, vendors, deptRoleMap, onClose, onAssign }) => {
  const isBulk = !!task._bulk;
  const dept = task._dept || '';
  // For bulk mode the dept may be 'mixed' (when the user selected rows across depts)
  // — fall back to no role-matching in that case.
  const wantedRoles = (dept === 'mixed' ? [] : (deptRoleMap[dept] || []));
  // Animatics in the UI is the merge of storyboard + animatics task_types — pull
  // both role recommendations so the picker still surfaces the right artists.
  if (dept === 'animatics') {
    (deptRoleMap.storyboard || []).forEach(r => { if (!wantedRoles.includes(r)) wantedRoles.push(r); });
  }
  const matches = (u) => wantedRoles.length > 0 && (u.specialist_roles || []).some(r => wantedRoles.includes(r));
  const sortByMatch = (a, b) => (matches(b) ? 1 : 0) - (matches(a) ? 1 : 0);
  const all = [...internal, ...freelancers].sort(sortByMatch);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5 w-[32rem] max-h-[75vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="sp-assign-modal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-[9px] text-fuchsia-400 uppercase tracking-wide">
              {isBulk ? `Bulk Assign · ${(task._ids || []).length} task${(task._ids || []).length === 1 ? '' : 's'}` : `Assign · ${dept?.toUpperCase()}`}
            </p>
            <h3 className="text-sm font-heading font-semibold text-white mt-0.5">{task.title}</h3>
            {!isBulk && (
              <p className="text-[10px] text-zinc-500">Parent: {task.parent_code || '—'} · Deadline: {task.deadline ? task.deadline.slice(0, 10) : 'Not set'}</p>
            )}
            {isBulk && (
              <p className="text-[10px] text-zinc-500">Pick a single artist — every task in this batch will be allotted to them.</p>
            )}
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-zinc-500 hover:text-white" /></button>
        </div>
        <div className="flex-1 overflow-y-auto space-y-1">
          {all.length === 0 && vendors.length === 0 && (
            <div className="text-center py-8">
              <AlertTriangle className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
              <p className="text-xs text-zinc-500">No workforce available</p>
            </div>
          )}
          {all.map(u => {
            const m = matches(u);
            return (
              <button
                key={u.id}
                onClick={() => onAssign(u.id)}
                data-testid={`sp-assign-pick-${u.id}`}
                className={`w-full text-left px-3 py-2 border rounded-sm flex items-center justify-between transition-all ${m ? 'bg-emerald-500/5 border-emerald-500/30 hover:border-emerald-500/60' : 'bg-zinc-800/30 border-zinc-800/50 hover:border-zinc-700'}`}
              >
                <div className="min-w-0">
                  <p className="text-xs text-zinc-200 truncate">
                    {u.full_name} {m && <span className="text-[8px] text-emerald-400 ml-1">MATCH</span>}
                  </p>
                  <p className="text-[9px] text-zinc-600 truncate">{(u.specialist_roles || []).join(', ').replace(/_/g, ' ') || u.email}</p>
                </div>
                <WorkforceBadge user={u} />
              </button>
            );
          })}
          {vendors.length > 0 && (
            <div className="pt-2 mt-2 border-t border-zinc-800">
              <p className="text-[9px] text-violet-400 uppercase mb-1 font-medium">Vendor Studios (Bulk)</p>
              {vendors.map(v => (
                <button
                  key={v.id}
                  onClick={() => onAssign(v.user_id)}
                  data-testid={`sp-assign-vendor-${v.id}`}
                  className="w-full text-left px-3 py-2 bg-zinc-800/30 border border-zinc-800/50 rounded-sm hover:border-violet-500/50 flex items-center justify-between mb-1"
                >
                  <div>
                    <p className="text-xs text-zinc-200">{v.company_name}</p>
                    <p className="text-[9px] text-zinc-600">Team: {v.total_team_size || 0}</p>
                  </div>
                  <WorkforceBadge user={{ id: v.id, workforce_type: 'vendor_producer', role: 'vendor_producer' }} />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubmissionPipelinePanel;

/**
 * PIPELINE REFORM R6 — Approval Engine empty-state.
 * Renders on asset-mode tabs (Asset Build, Rigging) when no tasks exist.
 * Fetches the count of approved upstream items waiting to be promoted and
 * offers a one-click "Promote N approved" button that materialises them.
 */
function ApprovalEngineEmptyState({ api, projectId, target, onPromoted }) {
  const [count, setCount] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [healBusy, setHealBusy] = React.useState(false);
  const [unblockBusy, setUnblockBusy] = React.useState(false);
  const [diagBusy, setDiagBusy] = React.useState(false);
  const [diagnostic, setDiagnostic] = React.useState(null);
  const [msg, setMsg] = React.useState('');
  const [err, setErr] = React.useState('');
  const [healResponse, setHealResponse] = React.useState(null);
  React.useEffect(() => {
    if (!api || !projectId || !target) return;
    (async () => {
      try {
        const r = await api.get(`/projects/${projectId}/pipeline/promotable-count?target=${target}`);
        setCount(r.data);
      } catch (e) {
        setCount({ approved_upstream: 0, waiting_for_promotion: 0 });
      }
    })();
  }, [api, projectId, target]);
  const promote = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await api.post(`/projects/${projectId}/pipeline/auto-promote-approved`);
      const created = (r.data.created || []).length;
      const repaired = r.data.tasks_state_repaired || 0;
      const repairBit = repaired > 0 ? ` (${repaired} legacy approval${repaired === 1 ? '' : 's'} repaired)` : '';
      setMsg(`✓ ${created} task${created === 1 ? '' : 's'} created from approved upstream items${repairBit}.`);
      if (onPromoted) onPromoted();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Promote failed');
    } finally { setBusy(false); }
  };
  const heal = async () => {
    setHealBusy(true); setErr(''); setMsg(''); setHealResponse(null);
    try {
      const r = await api.post(`/projects/${projectId}/pipeline/heal-chain`);
      setHealResponse(r.data);
      const orphans = (r.data.orphans_fixed || []).length;
      const drift = r.data.drift_repaired || 0;
      const promoted = (r.data.auto_promoted || []).length;
      const before = r.data.stages_present_before || {};
      const after = r.data.stages_present_after || {};
      const detail = Object.keys(after).map(k => {
        const b = before[k] || 0; const a = after[k] || 0;
        return a !== b ? `${k}: ${b}→${a}` : null;
      }).filter(Boolean).join(' · ');
      setMsg(`✓ Pipeline healed — ${orphans} orphan${orphans === 1 ? '' : 's'} reconstructed, ${drift} drift repaired, ${promoted} auto-promoted${detail ? ` · ${detail}` : ''}.`);
      if (onPromoted) onPromoted();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Heal failed');
    } finally { setHealBusy(false); }
  };
  const runDiagnostic = async () => {
    setDiagBusy(true); setErr(''); setDiagnostic(null);
    try {
      const r = await api.get(`/admin/pipeline/diagnose/${projectId}`);
      setDiagnostic(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Diagnostic failed');
    } finally { setDiagBusy(false); }
  };
  const unblockStuck = async () => {
    setUnblockBusy(true); setErr(''); setMsg('');
    try {
      const r = await api.post(`/admin/pipeline/unblock-stuck-downstream/${projectId}`);
      const stages = r.data.healed_by_upstream_stage || {};
      const total = Object.values(stages).reduce((s, n) => s + n, 0);
      const detail = Object.entries(stages).map(([k, v]) => `${k}: ${v}`).join(' · ');
      setMsg(`✓ Unblocked ${total} stuck downstream task${total === 1 ? '' : 's'}${detail ? ` (${detail})` : ''}. Scanned ${r.data.scanned_approved_submissions} approved upstreams.`);
      if (onPromoted) onPromoted();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Unblock failed');
    } finally { setUnblockBusy(false); }
  };
  const upstream = (count?.upstream_type || '').replace(/_/g, ' ');
  const waitingN = count?.waiting_for_promotion || 0;
  const anyN = count?.approved_upstream || 0;
  return (
    <div className="bg-gradient-to-br from-emerald-500/5 to-cyan-500/5 border border-emerald-500/30 rounded-md text-center py-10 px-6"
         data-testid={`sp-approval-engine-empty-${target}`}>
      <CheckCircle2 className="w-10 h-10 text-emerald-400/70 mx-auto mb-3" />
      <p className="text-sm text-zinc-200 font-medium mb-1">
        Waiting for approved {upstream || 'upstream'}
      </p>
      <p className="text-[11px] text-zinc-500 max-w-md mx-auto leading-relaxed mb-4">
        This stage auto-populates the moment an upstream {upstream || 'item'} is client-approved.{' '}
        {waitingN > 0
          ? `${waitingN} approved upstream item${waitingN === 1 ? '' : 's'} are waiting to be promoted.`
          : (anyN > 0
              ? `${anyN} approved upstream item${anyN === 1 ? '' : 's'} already promoted into this stage's chain.`
              : 'No approved upstream yet — once Concept Art / Asset Build is client-approved, work will appear here automatically.')}
      </p>
      <div className="flex items-center justify-center gap-2 flex-wrap">
        {waitingN > 0 && (
          <button onClick={promote} disabled={busy || healBusy || diagBusy}
            data-testid="sp-promote-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white text-xs font-medium shadow-lg shadow-emerald-500/20 disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {busy ? 'Promoting…' : `Promote ${waitingN} approved ${upstream}`}
          </button>
        )}
        <button onClick={heal} disabled={busy || healBusy || diagBusy}
          data-testid="sp-heal-btn"
          title="Deep-heal: reconstructs missing intermediate stages and re-runs auto-promotion."
          className="inline-flex items-center gap-2 px-3 py-2 rounded border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 text-xs font-medium disabled:opacity-50">
          {healBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span aria-hidden>🩺</span>}
          {healBusy ? 'Healing…' : 'Heal Pipeline'}
        </button>
        <button onClick={runDiagnostic} disabled={busy || healBusy || diagBusy || unblockBusy}
          data-testid="sp-diagnose-btn"
          title="Show full diagnostic data — why is this stage empty? Useful for debugging."
          className="inline-flex items-center gap-2 px-3 py-2 rounded border border-zinc-600 text-zinc-300 hover:bg-zinc-800 text-xs font-medium disabled:opacity-50">
          {diagBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span aria-hidden>🔍</span>}
          {diagBusy ? 'Scanning…' : 'Show Diagnostic'}
        </button>
        <button onClick={unblockStuck} disabled={busy || healBusy || diagBusy || unblockBusy}
          data-testid="sp-unblock-stuck-btn"
          title="Find tasks stuck 'Waiting on upstream' even though upstream is approved (parent_id fallback). One-click heal."
          className="inline-flex items-center gap-2 px-3 py-2 rounded border border-amber-500/50 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 text-xs font-medium disabled:opacity-50">
          {unblockBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span aria-hidden>🔓</span>}
          {unblockBusy ? 'Unblocking…' : 'Unblock Stuck Downstream'}
        </button>
      </div>
      {msg && (
        <p className="text-[11px] text-emerald-300 mt-3" data-testid="sp-promote-msg">{msg}</p>
      )}
      {err && (
        <p className="text-[11px] text-rose-300 mt-3">{err}</p>
      )}
      {diagnostic && <DiagnosticReport data={diagnostic} />}
      {healResponse && <HealDetailReport data={healResponse} />}
      {/* Add-Asset modal — founder ask May 16, 2026: production-only
          props that bypass client approval, go straight to asset_build
          unassigned, ready for artist allotment. */}
      {showAddAsset && (
        <AddProductionAssetModal
          projectId={projectId}
          onClose={() => setShowAddAsset(false)}
          onCreated={async () => {
            setShowAddAsset(false);
            await load();
          }}
        />
      )}
    </div>
  );
}

function DiagnosticReport({ data }) {
  return (
    <div className="mt-5 text-left max-w-3xl mx-auto bg-zinc-900/70 border border-zinc-700 rounded-md p-4 space-y-3"
         data-testid="sp-diagnostic-panel">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-bold text-cyan-300 uppercase tracking-wide">Pipeline Diagnostic</h4>
        <span className="text-[10px] text-zinc-500">{data.generated_at}</span>
      </div>
      {data.project && (
        <div className="text-[11px] text-zinc-400">
          <span className="text-zinc-500">Project:</span> {data.project.name} · kind={data.project.kind || '—'} · id=<code className="text-zinc-300">{data.project.id}</code>
          {data.project.parent_id && <> · parent=<code className="text-zinc-300">{data.project.parent_id}</code></>}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-[11px]">
        <DiagStat label="Approved submissions" value={data.approved_submissions_count} tone={data.approved_submissions_count > 0 ? 'emerald' : 'zinc'} />
        <DiagStat label="With EMPTY item_refs" value={data.approvals_with_empty_item_refs}
          tone={data.approvals_with_empty_item_refs > 0 ? 'rose' : 'zinc'}
          hint={data.approvals_with_empty_item_refs > 0 ? 'Legacy schema — click Heal Pipeline to resolve via item_id fallback.' : ''}/>
        <DiagStat label="Orphan refs" value={(data.approvals_with_orphan_refs || []).length}
          tone={(data.approvals_with_orphan_refs || []).length > 0 ? 'amber' : 'zinc'}
          hint={(data.approvals_with_orphan_refs || []).length > 0 ? 'Submission points at tasks that don\'t exist in this project.' : ''}/>
        <DiagStat label="Chain gaps" value={(data.chain_gaps || []).length}
          tone={(data.chain_gaps || []).length > 0 ? 'amber' : 'zinc'}
          hint={(data.chain_gaps || []).length > 0 ? 'Parents missing an intermediate stage (e.g., Rigging but no Asset Build).' : ''}/>
        <DiagStat label="Hidden by placeholder filter" value={data.tasks_hidden_by_placeholder_filter}
          tone={data.tasks_hidden_by_placeholder_filter > 0 ? 'amber' : 'zinc'}
          hint={data.tasks_hidden_by_placeholder_filter > 0 ? 'Legacy tasks without is_placeholder=True — fixed in Session 46.' : ''}/>
        <DiagStat label="Total tasks in project" value={Object.values(data.task_counts || {}).reduce((s, n) => s + n, 0)} tone="zinc"/>
      </div>
      <div className="space-y-1">
        <div className="text-[11px] text-zinc-400 font-semibold">Task counts by type:</div>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(data.task_counts || {}).map(([k, v]) => (
            <span key={k} className="px-2 py-0.5 rounded bg-zinc-800 border border-zinc-700 text-[10px] text-zinc-300 font-mono">
              {k}: <b className="text-white">{v}</b>
            </span>
          ))}
          {Object.keys(data.task_counts || {}).length === 0 && <span className="text-[10px] text-zinc-500">No tasks.</span>}
        </div>
      </div>
      {(data.approved_submissions_sample || []).length > 0 && (
        <details className="text-[11px]">
          <summary className="cursor-pointer text-zinc-400 hover:text-zinc-200">
            Sample approved submissions ({data.approved_submissions_sample.length} shown)
          </summary>
          <div className="mt-2 space-y-1 ml-3 font-mono text-[10px] text-zinc-400">
            {data.approved_submissions_sample.map(s => (
              <div key={s.id} className="py-1 border-l-2 border-zinc-700 pl-2">
                <div className="text-zinc-200">{s.title} <span className="text-zinc-500">({s.stage})</span></div>
                <div>item_id=<code className="text-cyan-300">{s.item_id || 'null'}</code> · item_refs={JSON.stringify(s.item_refs || [])}</div>
                <div>status={s.status} · decision={s.client_decision}</div>
              </div>
            ))}
          </div>
        </details>
      )}
      {(data.chain_gaps || []).length > 0 && (
        <details className="text-[11px]">
          <summary className="cursor-pointer text-amber-400 hover:text-amber-300">
            Chain gaps ({data.chain_gaps.length}) — click Heal Pipeline to reconstruct
          </summary>
          <div className="mt-2 space-y-1 ml-3 font-mono text-[10px] text-zinc-400">
            {data.chain_gaps.slice(0, 10).map(g => (
              <div key={g.parent_id} className="py-1 border-l-2 border-amber-700 pl-2">
                <div>parent=<code className="text-amber-300">{g.parent_id}</code> · cat={g.category}</div>
                <div>present: {g.stages_present.join(', ') || '(none)'}</div>
                <div className="text-rose-400">missing: {g.stages_missing.join(', ')}</div>
              </div>
            ))}
            {data.chain_gaps.length > 10 && <div className="text-zinc-500">… and {data.chain_gaps.length - 10} more.</div>}
          </div>
        </details>
      )}
    </div>
  );
}

function DiagStat({ label, value, tone = 'zinc', hint }) {
  const toneClass = {
    zinc: 'text-zinc-300 border-zinc-700',
    emerald: 'text-emerald-300 border-emerald-700',
    rose: 'text-rose-300 border-rose-700',
    amber: 'text-amber-300 border-amber-700',
  }[tone];
  return (
    <div className={`rounded border px-3 py-2 bg-zinc-900/50 ${toneClass}`} title={hint || ''}>
      <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className="text-lg font-bold mt-0.5">{value}</div>
      {hint && <div className="text-[10px] text-zinc-500 mt-1 leading-tight">{hint}</div>}
    </div>
  );
}

function HealDetailReport({ data }) {
  return (
    <details className="mt-3 text-left max-w-3xl mx-auto text-[11px]" data-testid="sp-heal-detail">
      <summary className="cursor-pointer text-emerald-400 hover:text-emerald-300">
        Heal run details ({(data.orphans_fixed || []).length + (data.auto_promoted || []).length + (data.drift_repaired || 0)} changes)
      </summary>
      <div className="mt-2 space-y-2 bg-zinc-900/70 border border-zinc-700 rounded p-3 font-mono text-[10px] text-zinc-400">
        {(data.auto_promoted || []).length > 0 && (
          <div>
            <div className="text-emerald-400 font-semibold mb-1">auto_promoted ({data.auto_promoted.length}):</div>
            {data.auto_promoted.slice(0, 15).map((p, i) => (
              <div key={i} className="pl-2 border-l-2 border-emerald-700">
                {p.from_type} → {p.to_type} · parent=<code className="text-zinc-300">{p.parent_id}</code>
              </div>
            ))}
            {data.auto_promoted.length > 15 && <div className="text-zinc-500 pl-2">… +{data.auto_promoted.length - 15} more</div>}
          </div>
        )}
        {(data.orphans_fixed || []).length > 0 && (
          <div>
            <div className="text-amber-400 font-semibold mb-1">orphans_fixed ({data.orphans_fixed.length}):</div>
            {data.orphans_fixed.slice(0, 15).map((o, i) => (
              <div key={i} className="pl-2 border-l-2 border-amber-700">
                +{o.missing_intermediate} (status={o.new_status}) for {o.downstream_type}@<code className="text-zinc-300">{o.parent_id}</code>
              </div>
            ))}
          </div>
        )}
        <div className="pt-2 border-t border-zinc-700">
          <span className="text-zinc-500">before:</span> {JSON.stringify(data.stages_present_before || {})}<br />
          <span className="text-zinc-500">after:</span> <span className="text-emerald-300">{JSON.stringify(data.stages_present_after || {})}</span>
        </div>
      </div>
    </details>
  );
}
